import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useCurrentUser } from "@hooks/use-current-user";
import { usePrice } from "@hooks/use-prices";
import ahoy from "@lib/ahoy";
import Modal from "@components/core/Modal";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import Badge from "@components/core/Badge";
import { getFeaturedPlan } from "@utils/call-api/plan-call";

function UnsubscribeModalRetentionMessage({ opened, close, currentCoupon }) {
  const t = useTranslations("accounts");

  const user = useCurrentUser();
  const price = usePrice();

  const planPrice = user?.subscription?.plan_price;

  const [featuredPlanAmount, setFeaturedPlanAmount] = useState(0);

  useEffect(() => {
    async function fetchFeaturedPlanAmount() {
      const featuredPlan = await getFeaturedPlan();
      setFeaturedPlanAmount(featuredPlan.amount_inc_tax);
    }

    fetchFeaturedPlanAmount();
  }, []);

  function sendEventUserReallkyAskForCancelation() {
    ahoy("$click", { button: "reallyWantUnsubscribe" });
    // eslint-disable-next-line no-undef
    if (typeof _cio !== "undefined") _cio.track("ClickReallyWantUnsubscribe");
  }

  const discountValueText = () => {
    if (currentCoupon.type === "coupon_percent_off") {
      return t(`manage_subscription.coupon.coupon_percent_off`, { percent: currentCoupon.value });
    } else {
      return t(`manage_subscription.coupon.coupon_amount_off`, {
        amount: price(currentCoupon.value, { withoutSpace: true }),
      });
    }
  };

  const discountDurationText = () => {
    switch (currentCoupon.duration) {
      case "once":
        return t("manage_subscription.coupon.once");
      case "forever":
        return t("manage_subscription.coupon.forever");
      case "repeating":
        return t("manage_subscription.coupon.repeating", {
          count: parseInt(currentCoupon.duration_in_months, 10),
        });
      default:
        return null;
    }
  };

  function displayCurrentCoupon() {
    if (!currentCoupon) return null;

    return (
      <p className="bg-tertiary py-2 px-3 text-white text-center">
        <span>{discountValueText()}</span>
        <span>{discountDurationText()}</span>
      </p>
    );
  }

  return (
    <Modal
      show={opened}
      onHide={close}
      title={t("unsubscribe_modal.retention.header")}
      fullscreen="sm-down"
    >
      <div className="p-3">
        <div className="pt-3 p-sm-3 text-center h-100 d-flex flex-column justify-content-between">
          <div className="mb-4">
            <h3
              className="mb-4 mx-auto"
              style={{ maxWidth: "380px" }}
            >
              {t("unsubscribe_modal.retention.title")}
            </h3>
            <p className="mb-3">{t("unsubscribe_modal.retention.subtitle")}</p>
            <div className="pb-4 d-flex flex-column align-items-center">
              {user?.badge && (
                <Badge
                  className="mb-3"
                  variant="light-primary"
                >
                  {t("unsubscribe_modal.retention.badge", { percent: user.badge.percent_off })}
                </Badge>
              )}

              <p className="mb-0 text-primary font-weight-bold">
                {t("unsubscribe_modal.retention.unlimited_plan", {
                  amount: price(planPrice.amount_inc_tax, { withoutSpace: true }),
                })}
              </p>
              <p className="mb-3 text-primary font-weight-bold">
                {t("unsubscribe_modal.retention.name.swapable_between_families", {
                  count: planPrice.plan.accessories + planPrice.plan.clothes,
                })}
              </p>

              {displayCurrentCoupon()}
            </div>

            <p className="my-5">
              {t.rich("unsubscribe_modal.retention.new_plan_explanation", {
                amount: price(featuredPlanAmount, { withoutSpace: true }),
                span: (chunks) => <span className="text-primary font-weight-bold">{chunks}</span>,
              })}
            </p>
          </div>

          <div>
            <Button onClick={close}>{t("unsubscribe_modal.retention.stay")}</Button>

            <div className="py-3">
              <Link
                href="/profile/manage-subscription/cancelation"
                className="link-primary no-underline"
                onClick={sendEventUserReallkyAskForCancelation}
              >
                {t("unsubscribe_modal.retention.leave")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default UnsubscribeModalRetentionMessage;
